import { type Order } from '../orders/orders-api';
import { type Store } from '../store/store-api';
import { offersDelivery } from '../store/store-model';
import { type Quote } from './checkout-api';

/**
 * The Checkout page's pure rules: what the frame validates, how a paid order
 * is recognised, and the few labels drawn from the platform's own records.
 * Nothing here computes a price — every number on the page is the quote's.
 */

export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

export interface GuestDetails {
  readonly name: string;
  readonly email: string;
  readonly phone: string;
}

export interface AddressFields {
  readonly a1: string;
  readonly a2: string;
  readonly town: string;
  readonly pc: string;
}

export type FieldErrors = Partial<Record<'name' | 'email' | 'phone' | 'a1' | 'town' | 'pc', string>>;

/** The frame's own messages for the guest's details. */
export function detailErrors(details: GuestDetails): FieldErrors {
  const errors: FieldErrors = {};
  if (details.name.trim().length < 2) errors.name = 'Enter your full name';
  if (!EMAIL.test(details.email.trim())) errors.email = 'Enter a valid email address';
  if (details.phone.replace(/\D/g, '').length < 10) errors.phone = 'Enter a UK mobile number';
  return errors;
}

/** The frame's own messages for a typed delivery address. */
export function addressErrors(address: AddressFields): FieldErrors {
  const errors: FieldErrors = {};
  if (address.a1.trim().length < 3) errors.a1 = 'Enter the first line of your address';
  if (address.town.trim().length < 2) errors.town = 'Enter a town or city';
  if (!UK_POSTCODE.test(address.pc.trim())) errors.pc = 'Enter a valid UK postcode';
  return errors;
}

/** Whether the shop offers this way of getting the order, in the storefront's own vocabulary. */
export const deliversFrom = offersDelivery;

export function collectsFrom(store: Pick<Store, 'fulfilmentModes'>): boolean {
  return store.fulfilmentModes.includes('COLLECTION');
}

/**
 * Paid is the webhook's word, read off the order — never this page's.
 *
 * An order that needs a prescriber is paid and then held (AWAITING_REVIEW), so
 * the payment status decides, exactly as the Android app's PaymentViewModel.
 */
export type PaymentOutcome = 'PAID' | 'FAILED' | 'WAITING' | 'CLOSED';

export function paymentOutcome(order: Pick<Order, 'status' | 'payment'>): PaymentOutcome {
  if (order.payment.status === 'PAID' || order.status === 'CONFIRMED') return 'PAID';
  if (order.status === 'CANCELLED' || order.payment.status === 'REFUNDED') return 'CLOSED';
  if (order.payment.status === 'FAILED') return 'FAILED';
  return 'WAITING';
}

/** The polling budget: the Android app's ten reads, a second and a half apart. Mutable for tests only. */
export const polling = { attempts: 10, delayMs: 1_500 };

/** The first line whose price moved between two quotes, for the frame's "A price has changed". */
export function priceChange(
  before: Quote,
  after: Quote,
): { readonly name: string; readonly oldMinor: number; readonly nowMinor: number } | undefined {
  const old = new Map(before.lines.map((line) => [line.lineId, line]));
  for (const line of after.lines) {
    const was = old.get(line.lineId);
    if (was !== undefined && was.unitPriceMinor !== line.unitPriceMinor) {
      return { name: line.title, oldMinor: was.lineTotalMinor, nowMinor: line.lineTotalMinor };
    }
  }
  return undefined;
}

// -- One idempotency key per payment attempt ----------------------------------------

const KEY_PREFIX = 'jt-pay-key:';

function newKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return `web-pay-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * The attempt's key: made once, reused for every retry of that attempt (a
 * reload, a dropped connection), so the platform opens the same payment rather
 * than a second one. Kept in this tab's sessionStorage — it is not a secret.
 */
export function attemptKey(orderId: string): string {
  try {
    const kept = window.sessionStorage.getItem(KEY_PREFIX + orderId);
    if (kept !== null && kept.length >= 8) return kept;
    const key = newKey();
    window.sessionStorage.setItem(KEY_PREFIX + orderId, key);
    return key;
  } catch {
    return newKey();
  }
}

/** A deliberate new attempt is a new key — after a decline, as Android's retry. */
export function forgetAttempt(orderId: string): void {
  try {
    window.sessionStorage.removeItem(KEY_PREFIX + orderId);
  } catch {
    // Nothing kept; the next attempt makes a new key anyway.
  }
}

/** "Thanks, Sam." from the person's own name, or no name at all. */
export function thanksTitle(name: string | undefined): string {
  const first = name?.trim().split(/\s+/)[0];
  return first !== undefined && first !== ''
    ? `Thanks, ${first}. Your order is placed.`
    : 'Thanks. Your order is placed.';
}

/** "Medicines are supplied by X, a GPhC-registered pharmacy" — only when the shop's own record says so. */
export function suppliedBy(store: Pick<Store, 'name' | 'registration'> | undefined, name: string): string {
  const regulator = store?.registration?.regulator.toUpperCase().replace(/[^A-Z]/g, '');
  return regulator === 'GPHC'
    ? `Medicines are supplied by ${name}, a GPhC-registered pharmacy.`
    : `Medicines are supplied by ${name}.`;
}

/** Where the shop is, as its record publishes it: town and postcode, nothing guessed. */
export function shopAddress(store: Pick<Store, 'locations'> | undefined): string | undefined {
  const place = store?.locations.find(
    (location) => location.town !== undefined || location.postcode !== undefined,
  );
  if (place === undefined) return undefined;
  return [place.town, place.postcode].filter((part) => part !== undefined && part !== '').join(' ');
}
